import React, { useContext, useState } from 'react'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import CloseIcon from '@mui/icons-material/Close'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined'
import UnfoldMoreDoubleRoundedIcon from '@mui/icons-material/UnfoldMoreDoubleRounded'
import AnDateField from '../../../../../components/DateTime/DateField'
import API from '../../../../../services/API'
import DocumentsField from '../../../../../helpers/DocumentsField'
import {
  DEMO_UPLOAD_MESSAGE,
  uploadDocument,
} from '../../../../../helpers/uploadDocument'
import { GlobalContext } from '../../../../../App'
import { VehicleContext } from '../../SingleVehicle'
import { Section } from '../ServicingMotInsurance/AddService'
import {
  defaultNextDue,
  LOLER_EQUIPMENT_TYPES,
  LOLER_EXAMINATION_TYPES,
  LOLER_OUTCOMES,
  lolerIntervalMonths,
} from './lolerOptions'

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`

// Record (or correct) one LOLER thorough examination. `existing` edits that
// record; `prefill` starts a new examination of equipment already on the
// vehicle (equipment details carried over, examination fields blank).
const AddLolerInspection = ({ existing, prefill, onClose, onSaved }) => {
  const { vehicle } = useContext(VehicleContext)
  const { setToast } = useContext(GlobalContext)
  const source = existing ?? prefill ?? {}
  const isEdit = !!existing?._id

  const [equipmentType, setEquipmentType] = useState(
    source.equipmentType ?? (vehicle?.hasTailLift ? 'Tail lift' : ''),
  )
  const [equipmentDescription, setEquipmentDescription] = useState(
    source.equipmentDescription ?? '',
  )
  const [serialNumber, setSerialNumber] = useState(source.serialNumber ?? '')
  const [safeWorkingLoadKg, setSafeWorkingLoadKg] = useState(
    source.safeWorkingLoadKg ?? '',
  )
  const [liftsPeople, setLiftsPeople] = useState(source.liftsPeople !== false)
  const [examinationType, setExaminationType] = useState(
    existing?.examinationType ?? LOLER_EXAMINATION_TYPES[0],
  )
  const [examinationDate, setExaminationDate] = useState(
    existing?.examinationDate ?? null,
  )
  const [nextExaminationDue, setNextExaminationDue] = useState(
    existing?.nextExaminationDue ?? null,
  )
  // Next-due follows the examination date (+6/12 months) until it's typed in
  const [nextDueEdited, setNextDueEdited] = useState(isEdit)
  const [examinedBy, setExaminedBy] = useState(existing?.examinedBy ?? '')
  const [examiningCompany, setExaminingCompany] = useState(
    existing?.examiningCompany ?? prefill?.examiningCompany ?? '',
  )
  const [reportNumber, setReportNumber] = useState(existing?.reportNumber ?? '')
  const [outcome, setOutcome] = useState(existing?.outcome ?? null)
  const [defects, setDefects] = useState(existing?.defects ?? '')
  const [remedialActionDue, setRemedialActionDue] = useState(
    existing?.remedialActionDue ?? null,
  )
  const [notes, setNotes] = useState(existing?.notes ?? '')
  const [files, setFiles] = useState([])
  const [removedDocuments, setRemovedDocuments] = useState([])
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState(null)

  const existingDocuments = existing?.documents ?? []

  const recomputeNextDue = (date, people) => {
    if (!nextDueEdited) setNextExaminationDue(defaultNextDue(date, people))
  }

  const toggleRemoveDocument = (key) =>
    setRemovedDocuments((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key],
    )

  const needsDefects = outcome && outcome !== 'Pass'
  const examValid =
    moment(examinationDate).isValid() &&
    !moment(examinationDate).isAfter(moment(), 'day')
  const nextValid =
    moment(nextExaminationDue).isValid() &&
    examValid &&
    moment(nextExaminationDue).isAfter(examinationDate, 'day')
  const canSave =
    !saving &&
    equipmentType.trim() &&
    examValid &&
    nextValid &&
    outcome &&
    (!needsDefects || defects.trim())

  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!canSave) return
    setSaving(true)
    setError(null)
    try {
      // One at a time - presigned URLs only live for 60s
      const uploadedKeys = []
      for (const file of files) {
        uploadedKeys.push(await uploadDocument(file, 'vehicles/documents'))
      }
      const body = {
        equipmentType: equipmentType.trim(),
        equipmentDescription,
        serialNumber,
        safeWorkingLoadKg,
        liftsPeople,
        examinationType,
        examinationDate,
        nextExaminationDue,
        examinedBy,
        examiningCompany,
        reportNumber,
        outcome,
        defects,
        remedialActionDue,
        notes,
        documents: [
          ...existingDocuments.filter((x) => !removedDocuments.includes(x)),
          ...uploadedKeys,
        ],
      }
      const res = isEdit
        ? await API.put(`/fleet/${vehicle._id}/loler/${existing._id}`, body)
        : await API.post(`/fleet/${vehicle._id}/loler`, body)
      if (res.status !== 200) {
        throw new Error(res.data?.error || 'Unable to save this examination.')
      }
      await Promise.allSettled(
        removedDocuments.map((key) => API.post('/deletes3object', { key })),
      )
      setToast({
        message: isEdit ? 'LOLER examination updated' : 'LOLER examination recorded',
      })
      onSaved(res.data)
      onClose()
    } catch (err) {
      console.error('Unable to save LOLER examination', err)
      setError(
        err?.message === DEMO_UPLOAD_MESSAGE
          ? DEMO_UPLOAD_MESSAGE
          : err?.message || 'Unable to save this examination. Please try again.',
      )
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setSaving(true)
    setError(null)
    const res = await API.delete(`/fleet/${vehicle._id}/loler/${existing._id}`)
    if (res.status === 200) {
      await Promise.allSettled(
        existingDocuments.map((key) => API.post('/deletes3object', { key })),
      )
      setToast({ message: 'LOLER examination deleted' })
      onSaved(res.data)
      onClose()
    } else {
      setError(res.data?.error || 'Unable to delete this examination.')
      setSaving(false)
      setConfirmDelete(false)
    }
  }

  const pendingChanges = [
    files.length > 0 && `${plural(files.length, 'file')} to upload`,
    removedDocuments.length > 0 &&
      `${plural(removedDocuments.length, 'document')} to remove`,
  ].filter(Boolean)

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}
    >
      {/* ---- Header ---- */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          px: { xs: 2, sm: 3 },
          py: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <UnfoldMoreDoubleRoundedIcon />
          </Box>
          <Box flex={1} minWidth={0}>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {isEdit ? 'Edit LOLER examination' : 'Record LOLER examination'}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {[vehicle?.callSign, vehicle?.regPlate].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          <Tooltip title="Close">
            <span>
              <IconButton onClick={onClose} size="small" aria-label="Close" disabled={saving}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {/* ---- Body ---- */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', bgcolor: 'background.default' }}>
        <Stack spacing={2} sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Section
            icon={<PrecisionManufacturingOutlinedIcon fontSize="small" />}
            title="Lifting equipment"
            caption="What was examined - re-examinations of the same equipment build up its history"
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  autoSelect
                  options={LOLER_EQUIPMENT_TYPES}
                  value={equipmentType}
                  onChange={(_e, value) => setEquipmentType(value ?? '')}
                  onInputChange={(_e, value) => setEquipmentType(value ?? '')}
                  disabled={!!prefill}
                  renderInput={(params) => (
                    <TextField {...params} label="Equipment" required />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value)}
                  label="Serial number"
                  autoComplete="off"
                  disabled={!!prefill}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  value={equipmentDescription}
                  onChange={(e) => setEquipmentDescription(e.target.value)}
                  label="Make / model"
                  placeholder="e.g. Ratcliff Palfinger RRP 300, rear"
                  autoComplete="off"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  value={safeWorkingLoadKg}
                  onChange={(e) => setSafeWorkingLoadKg(e.target.value)}
                  label="Safe working load"
                  autoComplete="off"
                  fullWidth
                  InputProps={{
                    inputProps: { min: 0, step: 1 },
                    endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={liftsPeople}
                      onChange={(e) => {
                        setLiftsPeople(e.target.checked)
                        recomputeNextDue(examinationDate, e.target.checked)
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Used to lift people
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Thorough examination every {lolerIntervalMonths(liftsPeople)} months
                        {liftsPeople ? ' (patients, wheelchair users, crew)' : ''}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          </Section>

          <Section
            icon={<FactCheckOutlinedIcon fontSize="small" />}
            title="Thorough examination"
            caption="From the competent person's report"
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <AnDateField
                  date={examinationDate}
                  onChange={(date) => {
                    setExaminationDate(date)
                    recomputeNextDue(date, liftsPeople)
                  }}
                  label="Examination date"
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AnDateField
                  date={nextExaminationDue}
                  onChange={(date) => {
                    setNextDueEdited(true)
                    setNextExaminationDue(date)
                  }}
                  label="Next examination due"
                  required
                  fullWidth
                />
              </Grid>
              {examinationDate && !examValid && (
                <Grid item xs={12}>
                  <Alert severity="warning">The examination date can't be in the future.</Alert>
                </Grid>
              )}
              {examValid && nextExaminationDue && !nextValid && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    The next examination must be after the examination date.
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  value={examinationType}
                  onChange={(e) => setExaminationType(e.target.value)}
                  label="Type of examination"
                  fullWidth
                >
                  {LOLER_EXAMINATION_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  value={reportNumber}
                  onChange={(e) => setReportNumber(e.target.value)}
                  label="Report / certificate number"
                  autoComplete="off"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  value={examinedBy}
                  onChange={(e) => setExaminedBy(e.target.value)}
                  label="Competent person"
                  autoComplete="off"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  value={examiningCompany}
                  onChange={(e) => setExaminingCompany(e.target.value)}
                  label="Company"
                  placeholder="e.g. engineering inspection provider"
                  autoComplete="off"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" fontWeight={600} mb={0.75}>
                  Outcome *
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={outcome}
                  onChange={(_e, value) => value && setOutcome(value)}
                  size="small"
                  sx={{ flexWrap: 'wrap' }}
                >
                  {LOLER_OUTCOMES.map((option) => (
                    <ToggleButton
                      key={option}
                      value={option}
                      color={option === 'Pass' ? 'success' : option === 'Fail' ? 'error' : 'warning'}
                      sx={{ textTransform: 'none', fontWeight: 600, px: 2 }}
                    >
                      {option}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>
              {outcome === 'Fail' && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    A failed thorough examination means the equipment must not be used until the
                    defect is put right. Consider reporting a vehicle defect too.
                  </Alert>
                </Grid>
              )}
              {needsDefects && (
                <>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      value={defects}
                      onChange={(e) => setDefects(e.target.value)}
                      label={outcome === 'Fail' ? 'Defects found' : 'Observations'}
                      autoComplete="off"
                      fullWidth
                      multiline
                      minRows={2}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <AnDateField
                      date={remedialActionDue}
                      onChange={(date) => setRemedialActionDue(date)}
                      label="Put right by"
                      fullWidth
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <TextField
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  label="Notes"
                  autoComplete="off"
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Grid>
            </Grid>
          </Section>

          <Section
            icon={<AttachFileRoundedIcon fontSize="small" />}
            title="Report & certificates"
            caption="Attach the examination report"
          >
            <DocumentsField
              existingDocuments={existingDocuments}
              removedDocuments={removedDocuments}
              onToggleRemove={toggleRemoveDocument}
              files={files}
              onFilesChange={setFiles}
              disabled={saving}
              caption="Report of thorough examination, certificates or photos — PDF, Word or images"
            />
          </Section>
        </Stack>
      </Box>

      {/* ---- Footer ---- */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          px: { xs: 2, sm: 3 },
          py: 1.5,
        }}
      >
        {confirmDelete ? (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ sm: 'center' }}
            spacing={1.5}
          >
            <Typography variant="body2" color="error.main" fontWeight={600}>
              Delete this examination record and its documents? Only do this for a record
              entered in error.
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button color="inherit" disabled={saving} onClick={() => setConfirmDelete(false)}>
                Keep
              </Button>
              <Button color="error" variant="contained" disabled={saving} onClick={handleDelete}>
                Delete
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ sm: 'center' }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              {isEdit && (
                <Button color="error" disabled={saving} onClick={() => setConfirmDelete(true)}>
                  Delete
                </Button>
              )}
              <Typography variant="caption" color="text.secondary">
                {saving && files.length
                  ? 'Uploading documents…'
                  : pendingChanges.length
                    ? pendingChanges.join(' · ')
                    : 'Fields marked * are required'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={onClose} color="inherit" disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={!canSave}>
                {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Record examination'}
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
    </Box>
  )
}

export default AddLolerInspection
